/**
 * Feature flag resolution — checks two sources in priority order:
 * 1. localStorage override — `ff_<name>` → 'true' / 'false'
 * 2. Vite env var fallback — `import.meta.env.VITE_FEATURE_<UPPER_SNAKE>`
 *
 * localStorage wins if set, allowing testers to toggle without redeploying.
 */

function toEnvVarKey(name: string): string {
  // camelCase → UPPER_SNAKE: e.g. "inlineDeals" → "INLINE_DEALS"
  return name.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toUpperCase();
}

/** Pure function — callable from non-React code (thunks, slices, etc.) */
export function getFeatureFlag(name: string): boolean {
  const localStorageKey = `ff_${name}`;
  const localValue = localStorage.getItem(localStorageKey);

  if (localValue === "true") return true;
  if (localValue === "false") return false;

  const envKey = `VITE_FEATURE_${toEnvVarKey(name)}`;
  const envValue = (import.meta.env as Record<string, string | undefined>)[envKey];

  return envValue === "true";
}

/** React hook wrapper — use in components */
export function useFeatureFlag(name: string): boolean {
  return getFeatureFlag(name);
}
